// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * keys.ts — desktop controls with proper DAS/ARR.
 *
 * Like touch.ts, this only ever QUEUES intents; the rAF step drains them, so
 * both input paths land on the same clock and neither can outrun the sim.
 */

/** Delay before a held direction starts auto-repeating. */
const DAS_MS = 150;
/** Auto-repeat interval once it does. */
const ARR_MS = 42;
/** Soft-drop repeat while ArrowDown is held. */
const SOFT_MS = 40;

export interface KeyIntents {
  moves: number;
  rotates: number;
  rotatesCcw: number;
  soft: number;
  hard: boolean;
  hold: boolean;
  pause: boolean;
}

export interface KeyLayer {
  drain(): KeyIntents;
  /** Called from the rAF step so auto-repeat is frame-driven, not timer-driven. */
  tick(dtMs: number): void;
  destroy(): void;
}

const empty = (): KeyIntents => ({
  moves: 0, rotates: 0, rotatesCcw: 0, soft: 0, hard: false, hold: false, pause: false,
});

export function attachKeys(target: Window | HTMLElement = window): KeyLayer {
  let q = empty();
  const held = new Set<string>();
  let dasDir = 0;
  let dasTimer = 0;
  let softTimer = 0;

  const onDown = (ev: Event) => {
    const e = ev as KeyboardEvent;
    if (e.repeat) return;
    const k = e.key;
    switch (k) {
      case 'ArrowLeft': case 'a': case 'A':
        held.add('L'); dasDir = -1; dasTimer = 0; q.moves -= 1; e.preventDefault(); break;
      case 'ArrowRight': case 'd': case 'D':
        held.add('R'); dasDir = 1; dasTimer = 0; q.moves += 1; e.preventDefault(); break;
      case 'ArrowDown': case 's': case 'S':
        held.add('D'); softTimer = 0; q.soft += 1; e.preventDefault(); break;
      case 'ArrowUp': case 'x': case 'X':
        q.rotates += 1; e.preventDefault(); break;
      case 'z': case 'Z':
        q.rotatesCcw += 1; e.preventDefault(); break;
      case ' ':
        q.hard = true; e.preventDefault(); break;
      case 'Shift': case 'c': case 'C':
        q.hold = true; e.preventDefault(); break;
      case 'p': case 'P': case 'Escape':
        q.pause = true; e.preventDefault(); break;
      default: break;
    }
  };

  const onUp = (ev: Event) => {
    const e = ev as KeyboardEvent;
    switch (e.key) {
      case 'ArrowLeft': case 'a': case 'A': held.delete('L'); break;
      case 'ArrowRight': case 'd': case 'D': held.delete('R'); break;
      case 'ArrowDown': case 's': case 'S': held.delete('D'); break;
      default: break;
    }
    if (!held.has('L') && !held.has('R')) dasDir = 0;
    else if (dasDir === -1 && !held.has('L')) { dasDir = 1; dasTimer = 0; }
    else if (dasDir === 1 && !held.has('R')) { dasDir = -1; dasTimer = 0; }
  };

  // A tab losing focus mid-hold would otherwise leave a key stuck down.
  const onBlur = () => { held.clear(); dasDir = 0; };

  target.addEventListener('keydown', onDown);
  target.addEventListener('keyup', onUp);
  window.addEventListener('blur', onBlur);

  return {
    drain() { const out = q; q = empty(); return out; },
    tick(dtMs) {
      if (dasDir !== 0) {
        dasTimer += dtMs;
        while (dasTimer >= DAS_MS + ARR_MS) { dasTimer -= ARR_MS; q.moves += dasDir; }
      } else dasTimer = 0;
      if (held.has('D')) {
        softTimer += dtMs;
        while (softTimer >= SOFT_MS) { softTimer -= SOFT_MS; q.soft += 1; }
      } else softTimer = 0;
    },
    destroy() {
      target.removeEventListener('keydown', onDown);
      target.removeEventListener('keyup', onUp);
      window.removeEventListener('blur', onBlur);
    },
  };
}
